import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { BsPaperclip, BsXLg, BsLockFill, BsFileEarmarkPdf } from "react-icons/bs";
import { fileName, isImage, when } from "./shared";

const MAX_FILES = 5;
const MAX_BYTES = 10 * 1024 * 1024;
const TYPES = ["image/jpeg", "image/png", "image/webp", "application/pdf"];

export const Attachments = ({ urls = [] }) => (urls.length ? (
    <div className="tk-files">
        {urls.map((url) => (
            <a key={url} href={url} target="_blank" rel="noreferrer" className="tk-file" title={fileName(url)}>
                {isImage(url) ? <img src={url} alt="" /> : <span className="tk-file-doc"><BsFileEarmarkPdf aria-hidden="true" /> {fileName(url)}</span>}
            </a>
        ))}
    </div>
) : null);

// The conversation: the raiser's description first, then every message. Internal notes are marked.
export const Thread = ({ ticket, messages = [], raiserType }) => {
    const end = useRef(null);
    useEffect(() => { end.current?.scrollIntoView?.({ block: "nearest" }); }, [messages.length]);
    const opening = { messageId: "opening", author: { type: ticket.raisedBy?.type || raiserType, name: ticket.raisedBy?.name }, body: ticket.description, attachments: ticket.attachments || [], createdAt: ticket.createdAt };
    return (
        <ol className="tk-thread" aria-label="Conversation">
            {[opening, ...messages].map((m) => {
                const fromRaiser = m.author?.type === (ticket.raisedBy?.type || raiserType);
                const side = m.internal ? "internal" : fromRaiser ? "them" : "us";
                return (
                    <li key={m.messageId} className={`tk-msg tk-msg-${side}`}>
                        <div className="tk-msg-head">
                            <span className="fw-semibold">{m.author?.name || (fromRaiser ? "Raiser" : "Team")}</span>
                            {m.author?.type && !fromRaiser ? <span className="tk-msg-role">{m.author.type}</span> : null}
                            {m.internal ? <span className="tk-msg-note"><BsLockFill aria-hidden="true" /> Internal note</span> : null}
                            <time className="tk-msg-time" dateTime={m.createdAt}>{when(m.createdAt)}</time>
                        </div>
                        {m.body ? <div className="tk-msg-body">{m.body}</div> : null}
                        <Attachments urls={m.attachments} />
                    </li>
                );
            })}
            <li ref={end} aria-hidden="true" />
        </ol>
    );
};

// Reply box. mode: "reply" | "note"; canReply / canNote decide which tabs show. onSend(fields, files) -> result.
export const Composer = ({ canReply, canNote, canWait, raiserLabel = "the customer", onSend, noteHint = "Only the team sees notes." }) => {
    const [mode, setMode] = useState(canReply ? "reply" : "note");
    const [body, setBody] = useState("");
    const [files, setFiles] = useState([]);
    const [wait, setWait] = useState(false);
    const [busy, setBusy] = useState(false);
    const input = useRef(null);
    useEffect(() => { if (!canReply && mode === "reply") setMode("note"); if (!canNote && mode === "note" && canReply) setMode("reply"); }, [canReply, canNote, mode]);
    if (!canReply && !canNote) return null;

    const pick = (event) => {
        const chosen = [...(event.target.files || [])];
        event.target.value = "";
        const bad = chosen.find((f) => !TYPES.includes(f.type) || f.size > MAX_BYTES);
        if (bad) { toast.error("Attach photos (JPG, PNG, WebP) or PDFs, each under 10 MB"); return; }
        const next = [...files, ...chosen].slice(0, MAX_FILES);
        if (files.length + chosen.length > MAX_FILES) toast.info(`Up to ${MAX_FILES} files`);
        setFiles(next);
    };
    const submit = async (event) => {
        event.preventDefault();
        if (!body.trim() && !files.length) return;
        setBusy(true);
        const result = await onSend({ body: body.trim(), internal: mode === "note", status: mode === "reply" && wait ? "Waiting on customer" : undefined }, files);
        setBusy(false);
        if (result?.ok) { setBody(""); setFiles([]); setWait(false); }
    };

    return (
        <form className={`tk-composer ${mode === "note" ? "is-note" : ""}`} onSubmit={submit}>
            {canReply && canNote ? (
                <div className="btn-group btn-group-sm mb-2" role="group" aria-label="Message type">
                    <button type="button" className={`btn ${mode === "reply" ? "btn-dark" : "btn-outline-secondary"}`} onClick={() => setMode("reply")} aria-pressed={mode === "reply"}>Reply to {raiserLabel}</button>
                    <button type="button" className={`btn ${mode === "note" ? "btn-warning" : "btn-outline-secondary"}`} onClick={() => setMode("note")} aria-pressed={mode === "note"}><BsLockFill aria-hidden="true" /> Internal note</button>
                </div>
            ) : null}
            <label className="visually-hidden" htmlFor="tk-body">{mode === "note" ? "Internal note" : "Reply"}</label>
            <textarea id="tk-body" className="form-control" rows={3} maxLength={4000} value={body} onChange={(e) => setBody(e.target.value)} placeholder={mode === "note" ? noteHint : `Write to ${raiserLabel}...`} />
            {files.length ? (
                <ul className="tk-chosen">
                    {files.map((f, i) => (
                        <li key={`${f.name}-${i}`}>{f.name} <button type="button" className="btn btn-link btn-sm p-0" aria-label={`Remove ${f.name}`} onClick={() => setFiles(files.filter((_, j) => j !== i))}><BsXLg aria-hidden="true" /></button></li>
                    ))}
                </ul>
            ) : null}
            <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                <input ref={input} type="file" multiple accept=".jpg,.jpeg,.png,.webp,.pdf" className="d-none" onChange={pick} />
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => input.current?.click()} disabled={files.length >= MAX_FILES}><BsPaperclip aria-hidden="true" /> Attach</button>
                {mode === "reply" && canWait ? (
                    <div className="form-check mb-0">
                        <input id="tk-wait" type="checkbox" className="form-check-input" checked={wait} onChange={(e) => setWait(e.target.checked)} />
                        <label className="form-check-label small" htmlFor="tk-wait">Wait for {raiserLabel}'s answer (pauses the SLA)</label>
                    </div>
                ) : null}
                <button type="submit" className={`btn btn-sm ms-auto ${mode === "note" ? "btn-warning" : "btn-primary"}`} disabled={busy || (!body.trim() && !files.length)}>
                    {busy ? "Sending..." : mode === "note" ? "Add note" : "Send reply"}
                </button>
            </div>
        </form>
    );
};

export const Activity = ({ events = [], labels }) => (
    <ol className="tk-activity">
        {events.map((e, i) => (
            <li key={`${e.action}-${i}`}>
                <div><span className="fw-semibold">{labels[e.action] || e.action}</span>{e.from || e.to ? <span className="text-muted small"> {e.from ? `${e.from} → ` : ""}{e.to || ""}</span> : null}</div>
                <div className="small text-muted">{e.by?.name || e.by?.id || "System"} · {when(e.at)}</div>
                {e.note ? <div className="small">{e.note}</div> : null}
            </li>
        ))}
    </ol>
);
